import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { addDays, format, parseISO } from 'date-fns';
import { deleteTimeslot, fetchAllWorkcells, fetchTimeslotImpact, fetchTimeslots, getCustomerId, searchTimeslots } from '../api';
import { useCustomerLocale } from '../context/CustomerSettingsContext';
import { useTranslation } from 'react-i18next';
import { formatLocalDate, formatLocalDateTime, parseLocalDateTime } from '../utils/localDateTime';

const todayString = () => formatLocalDate(new Date());

const formatDateTime = (value) => {
  if (!value) return '';
  return formatLocalDateTime(value, false) || value;
};

const formatDateLabel = (value, locale) => {
  if (!value) return '';
  const date = parseLocalDateTime(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
};

const formatTimeRange = (fromTime, toTime) => {
  const fromText = formatDateTime(fromTime);
  const toText = formatDateTime(toTime);
  if (!fromText && !toText) return '';
  return `${fromText || '?'} - ${toText || '?'}`;
};

const formatIdList = (ids) => {
  if (!Array.isArray(ids) || ids.length === 0) return '';
  return ids.join(', ');
};

const formatDateTimeList = (values) => {
  if (!Array.isArray(values) || values.length === 0) return '';
  return values.map((value) => formatDateTime(value)).filter(Boolean).join(', ');
};

const formatEntityList = (items) => {
  if (!Array.isArray(items) || items.length === 0) return '';
  return items.map((item) => item.name || item.id).join(', ');
};

const getRecordStatus = (record) => {
  if (record?.record_status) return record.record_status;
  if (record?.has_unmatched_planning) return 'unmatched_planning';
  if (Number(record?.occurrence_count || 0) > 1) return 'multiple_occurrences';
  return 'normal';
};

const getStatusColor = (status) => {
  if (status === 'unmatched_planning') return 'warning';
  if (status === 'multiple_occurrences') return 'info';
  return 'default';
};

const TimeslotStatusChip = ({ timeslot, t }) => {
  const status = getRecordStatus(timeslot);
  return (
    <Tooltip title={t(`deleteTimeslotPage.statusTooltips.${status}`)}>
      <Chip
        size="small"
        color={getStatusColor(status)}
        label={t(`deleteTimeslotPage.status.${status}`)}
        sx={{ fontWeight: 600 }}
      />
    </Tooltip>
  );
};

const moveDate = (date, days) => {
  const currentDate = parseISO(date || todayString());
  return format(addDays(currentDate, days), 'yyyy-MM-dd');
};

const CompactDateControl = ({ date, setDate, disabled, t }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '40px minmax(0, 180px) 40px',
      alignItems: 'center',
      gap: 0.75,
      width: { xs: '100%', sm: 'auto' }
    }}
  >
    <Tooltip title={t('deleteTimeslotPage.filters.previousDay')}>
      <span>
        <IconButton
          size="small"
          onClick={() => setDate(moveDate(date, -1))}
          disabled={disabled}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper'
          }}
        >
          <KeyboardArrowLeftIcon />
        </IconButton>
      </span>
    </Tooltip>

    <TextField
      label={t('deleteTimeslotPage.filters.date')}
      type="date"
      value={date}
      onChange={(event) => setDate(event.target.value)}
      size="small"
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      sx={{
        mb: 0,
        '& input': {
          fontVariantNumeric: 'tabular-nums'
        }
      }}
    />

    <Tooltip title={t('deleteTimeslotPage.filters.nextDay')}>
      <span>
        <IconButton
          size="small"
          onClick={() => setDate(moveDate(date, 1))}
          disabled={disabled}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper'
          }}
        >
          <KeyboardArrowRightIcon />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

const DeleteTimeslotPage = () => {
  const { t } = useTranslation();
  const locale = useCustomerLocale();

  const [customerId] = useState(getCustomerId());
  const [selectedDate, setSelectedDate] = useState(todayString());
  const [workcellId, setWorkcellId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearchId, setActiveSearchId] = useState('');
  const [workcells, setWorkcells] = useState([]);
  const [timeslots, setTimeslots] = useState([]);
  const [hasLoadedTimeslots, setHasLoadedTimeslots] = useState(false);
  const [loading, setLoading] = useState(false);
  const [workcellsLoading, setWorkcellsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [selectedTimeslot, setSelectedTimeslot] = useState(null);
  const [impact, setImpact] = useState(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const loadWorkcells = useCallback(async () => {
    try {
      setWorkcellsLoading(true);
      const data = await fetchAllWorkcells();
      setWorkcells(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load workcells', err);
    } finally {
      setWorkcellsLoading(false);
    }
  }, []);

  const loadTimeslots = useCallback(async () => {
    try {
      setHasLoadedTimeslots(true);
      setLoading(true);
      setError(null);
      setNotice(null);
      const data = activeSearchId
        ? await searchTimeslots(activeSearchId)
        : await fetchTimeslots(selectedDate, workcellId || null);
      setTimeslots(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load timeslots', err);
      setError(err.response?.data?.detail || t('deleteTimeslotPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeSearchId, selectedDate, workcellId, t]);

  useEffect(() => {
    loadWorkcells();
  }, [loadWorkcells]);

  const requestKey = activeSearchId
    ? `search:${activeSearchId}`
    : `date:${selectedDate}:workcell:${workcellId}`;
  const previousRequestKey = useRef(requestKey);

  useEffect(() => {
    if (previousRequestKey.current === requestKey) return;
    previousRequestKey.current = requestKey;
    loadTimeslots();
  }, [loadTimeslots, requestKey]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const normalizedSearch = searchInput.trim();
    if (!normalizedSearch) {
      setActiveSearchId('');
      return;
    }
    if (normalizedSearch === activeSearchId) {
      loadTimeslots();
      return;
    }
    setActiveSearchId(normalizedSearch);
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSearchId('');
  };

  const openImpactDialog = async (timeslot) => {
    setSelectedTimeslot(timeslot);
    setImpact(null);
    setImpactLoading(true);
    setDeleteReason('');
    setError(null);

    try {
      const data = await fetchTimeslotImpact(timeslot.timeslot_id);
      setImpact(data);
    } catch (err) {
      console.error('Failed to load timeslot impact', err);
      setSelectedTimeslot(null);
      setError(err.response?.data?.detail || t('deleteTimeslotPage.errors.impactFailed'));
    } finally {
      setImpactLoading(false);
    }
  };

  const closeImpactDialog = () => {
    if (deleting) return;
    setSelectedTimeslot(null);
    setImpact(null);
    setDeleteReason('');
  };

  const confirmDelete = async () => {
    if (!selectedTimeslot || deleting) return;

    try {
      setDeleting(true);
      const result = await deleteTimeslot(
        selectedTimeslot.timeslot_id,
        deleteReason.trim() || null
      );
      setSelectedTimeslot(null);
      setImpact(null);
      setDeleteReason('');
      await loadTimeslots();
      setNotice(t('deleteTimeslotPage.success.deleted', { id: result.timeslot_id }));
    } catch (err) {
      console.error('Failed to delete timeslot', err);
      setError(err.response?.data?.detail || t('deleteTimeslotPage.errors.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const isSearchActive = Boolean(activeSearchId);
  const customerLabel = customerId || t('common.na');
  const resultTitle = isSearchActive
    ? t('deleteTimeslotPage.results.searchTitle', { customerId: customerLabel, id: activeSearchId })
    : t('deleteTimeslotPage.results.dateTitle', { customerId: customerLabel, date: formatDateLabel(selectedDate, locale) });
  const emptyText = isSearchActive
    ? t('deleteTimeslotPage.emptySearch', { id: activeSearchId })
    : t('deleteTimeslotPage.empty');
  const isMultiOccurrenceImpact = Number(impact?.occurrence_count || 0) > 1;
  const hasUnmatchedPlanning = Boolean(impact?.has_unmatched_planning);
  const isMultiDateImpact = Array.isArray(impact?.affected_dates) && impact.affected_dates.length > 1;
  const isMultiProjectImpact = Array.isArray(impact?.affected_project_ids) && impact.affected_project_ids.length > 1;
  const isPlanningOnlyImpact = Number(impact?.assignment_rows || 0) === 0
    && (Number(impact?.daily_planning_rows || 0) > 0 || Number(impact?.project_planning_rows || 0) > 0);
  const shouldShowImpactWarning = Boolean(impact) && (
    isMultiOccurrenceImpact
    || hasUnmatchedPlanning
    || isMultiDateImpact
    || isMultiProjectImpact
    || isPlanningOnlyImpact
  );
  const occurrenceStartsText = formatDateTimeList(impact?.occurrence_starts);
  const impactTimeRangeText = formatTimeRange(impact?.from_time, impact?.to_time);
  const unmatchedPlanningRows = Number(impact?.unmatched_daily_planning_rows || 0)
    + Number(impact?.unmatched_project_planning_rows || 0);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        py: { xs: 2, md: 3 },
        px: { xs: 2, md: 3 },
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, letterSpacing: 0 }}>
          {t('deleteTimeslotPage.title')}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadTimeslots}
          disabled={loading}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          {t('deleteTimeslotPage.actions.refresh')}
        </Button>
      </Box>

      <Paper
        component="form"
        onSubmit={handleSearchSubmit}
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(247,249,252,0.96))',
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 1.15fr) auto minmax(240px, 0.65fr)' },
            gap: 1.5,
            alignItems: 'center',
            justifyItems: { xs: 'start', lg: 'stretch' }
          }}
        >
          <Box sx={{ minWidth: 0, width: { xs: 280, sm: '100%' }, maxWidth: '100%' }}>
            <TextField
              label={t('deleteTimeslotPage.filters.timeslotSearch')}
              placeholder={t('deleteTimeslotPage.filters.timeslotSearchPlaceholder')}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {(searchInput || activeSearchId) && (
                      <Tooltip title={t('deleteTimeslotPage.actions.clearSearch')}>
                        <IconButton size="small" onClick={clearSearch} edge="end">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('deleteTimeslotPage.actions.search')}>
                      <span>
                        <IconButton
                          type="submit"
                          size="small"
                          color="primary"
                          disabled={loading || !searchInput.trim()}
                          edge="end"
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
              sx={{
                minWidth: 0,
                mb: 0,
                width: { xs: 280, sm: '100%' },
                maxWidth: '100%'
              }}
            />
          </Box>

          <CompactDateControl
            date={selectedDate}
            setDate={setSelectedDate}
            disabled={loading}
            t={t}
          />

          <FormControl
            size="small"
            fullWidth
            sx={{
              mb: 0,
              width: { xs: 280, sm: '100%' },
              maxWidth: '100%'
            }}
          >
            <InputLabel>{t('deleteTimeslotPage.filters.workcell')}</InputLabel>
            <Select
              value={workcellId}
              label={t('deleteTimeslotPage.filters.workcell')}
              onChange={(event) => setWorkcellId(event.target.value)}
              disabled={workcellsLoading || loading}
            >
              <MenuItem value="">{t('deleteTimeslotPage.filters.allWorkcells')}</MenuItem>
              {workcells.map((workcell) => (
                <MenuItem key={workcell.workcell_id} value={String(workcell.workcell_id)}>
                  {workcell.workcell_name || `${t('deleteTimeslotPage.labels.workcell')} ${workcell.workcell_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: '0 12px 32px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {resultTitle}
          </Typography>
          <Chip
            size="small"
            label={t('deleteTimeslotPage.results.count', { count: timeslots.length })}
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('deleteTimeslotPage.table.timeslotId')}</TableCell>
              <TableCell>{t('deleteTimeslotPage.table.time')}</TableCell>
              <TableCell>{t('deleteTimeslotPage.table.status')}</TableCell>
              <TableCell>{t('deleteTimeslotPage.table.workcell')}</TableCell>
              <TableCell>{t('deleteTimeslotPage.table.operators')}</TableCell>
              <TableCell align="right">{t('deleteTimeslotPage.table.assignments')}</TableCell>
              <TableCell align="right">{t('deleteTimeslotPage.table.plannings')}</TableCell>
              <TableCell>{t('deleteTimeslotPage.table.projects')}</TableCell>
              <TableCell align="right">{t('deleteTimeslotPage.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3, px: 1 }}>
                    <CircularProgress size={24} />
                    <Typography>{t('common.loading')}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!loading && hasLoadedTimeslots && !error && timeslots.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary" sx={{ py: 3, px: 1 }}>
                    {emptyText}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && timeslots.map((timeslot) => (
              <TableRow key={`${timeslot.timeslot_id}-${timeslot.occurrence_start || timeslot.from_time || timeslot.date}`} hover>
                <TableCell>
                  <Typography component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {timeslot.timeslot_id}
                  </Typography>
                </TableCell>
                <TableCell>{formatTimeRange(timeslot.from_time, timeslot.to_time)}</TableCell>
                <TableCell><TimeslotStatusChip timeslot={timeslot} t={t} /></TableCell>
                <TableCell>{timeslot.workcell_name || t('common.na')}</TableCell>
                <TableCell>{formatIdList(timeslot.operator_names) || t('common.na')}</TableCell>
                <TableCell align="right">{timeslot.assignment_rows}</TableCell>
                <TableCell align="right">{timeslot.planning_count}</TableCell>
                <TableCell>{formatIdList(timeslot.project_ids) || t('common.na')}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={() => openImpactDialog(timeslot)}
                  >
                    {t('deleteTimeslotPage.actions.delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selectedTimeslot)} onClose={closeImpactDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('deleteTimeslotPage.dialog.title', { id: selectedTimeslot?.timeslot_id || '' })}</DialogTitle>
        <DialogContent dividers>
          {impactLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>{t('deleteTimeslotPage.dialog.loading')}</Typography>
            </Box>
          )}

          {!impactLoading && impact && (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <TimeslotStatusChip timeslot={impact} t={t} />
                <Typography variant="body2" color="text.secondary">
                  {t('deleteTimeslotPage.dialog.timeRange', {
                    range: impactTimeRangeText || t('common.na')
                  })}
                </Typography>
              </Box>
              {shouldShowImpactWarning && (
                <Alert severity={hasUnmatchedPlanning ? 'warning' : 'info'}>
                  {t('deleteTimeslotPage.dialog.globalDeleteNotice')}
                </Alert>
              )}
              {hasUnmatchedPlanning && (
                <Typography variant="body2" color="text.secondary">
                  {t('deleteTimeslotPage.dialog.unmatchedPlanningNotice', {
                    dates: formatIdList(impact.unmatched_dates) || t('common.na')
                  })}
                </Typography>
              )}
              {isMultiOccurrenceImpact && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {t('deleteTimeslotPage.dialog.occurrenceCount', { count: impact.occurrence_count })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('deleteTimeslotPage.dialog.occurrenceStarts', {
                      occurrences: occurrenceStartsText || t('common.na')
                    })}
                  </Typography>
                </>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                <Chip label={t('deleteTimeslotPage.dialog.assignmentRows', { count: impact.assignment_rows })} />
                <Chip label={t('deleteTimeslotPage.dialog.dailyPlanningRows', { count: impact.daily_planning_rows })} />
                <Chip label={t('deleteTimeslotPage.dialog.projectPlanningRows', { count: impact.project_planning_rows })} />
                {hasUnmatchedPlanning && (
                  <Chip label={t('deleteTimeslotPage.dialog.unmatchedPlanningRows', { count: unmatchedPlanningRows })} />
                )}
                <Chip label={t('deleteTimeslotPage.dialog.affectedProjects', {
                  projects: formatIdList(impact.affected_project_ids) || t('common.na')
                })} />
              </Box>
              <Typography>
                {t('deleteTimeslotPage.dialog.affectedDates', {
                  dates: formatIdList(impact.affected_dates) || t('common.na')
                })}
              </Typography>
              <Typography>
                {t('deleteTimeslotPage.dialog.affectedWorkcells', {
                  workcells: formatEntityList(impact.affected_workcells) || t('common.na')
                })}
              </Typography>
              <Typography>
                {t('deleteTimeslotPage.dialog.affectedOperators', {
                  operators: formatEntityList(impact.affected_operators) || t('common.na')
                })}
              </Typography>
              <TextField
                label={t('deleteTimeslotPage.dialog.reason')}
                value={deleteReason}
                onChange={(event) => setDeleteReason(event.target.value)}
                multiline
                minRows={2}
                fullWidth
                disabled={deleting}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImpactDialog} disabled={deleting}>
            {t('deleteTimeslotPage.actions.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            disabled={impactLoading || deleting || !impact}
          >
            {deleting ? t('deleteTimeslotPage.actions.deleting') : t('deleteTimeslotPage.actions.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeleteTimeslotPage;
